import Close16 from '@carbon/icons-react/es/close/16';
import React from 'react';
import { SimpleSearchFilter } from 'api/dashboard/widget';
import { View, ViewType } from 'api/views/types';
import { Filter } from 'components/molecules/filters/types';
import { defaultFilter } from 'components/molecules/filters/utils';
import {
  CreatableView,
  ViewDefinitionTransformer,
  Views,
} from 'components/molecules/views';
import { load } from 'components/molecules/views/core';
import { FilterableField } from 'components/organisms/tasks/fields';
import { FieldFilter } from 'components/organisms/tasks/filters';
import {
  from_filter,
  to_filter,
} from 'components/pages/api-filter-transformer';
import { ApiFilter } from 'utils/simple-search-filter/simple-search-filter';

export interface ApiDashboardFiltersViewDefinition {
  filter?: SimpleSearchFilter;
}
export interface DashboardFiltersViewDefinition {
  filter: Filter;
}

export const DEFAULT_VIEW_ID = -1;

export const getDefaultViews = (
  defaultViewName: string,
): Views<DashboardFiltersViewDefinition> =>
  load([
    {
      viewId: DEFAULT_VIEW_ID,
      viewName: defaultViewName,
      viewDefinition: {
        filter: defaultFilter,
      },
      createdBy: -1,
      viewIcon: <Close16 />,
      self: false,
      viewType: ViewType.Dashboard,
    },
  ]);

export const getViewDefinitionTransformer = (
  lookupByFieldName: Record<string, FilterableField>,
): ViewDefinitionTransformer<
  ApiDashboardFiltersViewDefinition,
  DashboardFiltersViewDefinition
> => ({
  from: ({
    filter: viewDefinitionFilter,
  }: ApiDashboardFiltersViewDefinition): DashboardFiltersViewDefinition => {
    return {
      filter: viewDefinitionFilter
        ? from_filter(lookupByFieldName)(viewDefinitionFilter)
        : defaultFilter,
    };
  },
  to: ({
    filter: viewDefinitionFilter,
  }: DashboardFiltersViewDefinition): ApiDashboardFiltersViewDefinition => {
    const isFilterValid =
      viewDefinitionFilter.criteria.filter(FieldFilter.isValid).length > 0;

    return {
      filter: isFilterValid ? to_filter(viewDefinitionFilter) : undefined,
    };
  },
});

export const isValidViewDefinition = (
  viewDefinition: DashboardFiltersViewDefinition,
): boolean => {
  return (
    viewDefinition.filter != null &&
    viewDefinition.filter.criteria.filter(FieldFilter.isValid).length > 0
  );
};

export const viewDefinitionComparator = (
  oldDefinition: DashboardFiltersViewDefinition,
  newDefinition: DashboardFiltersViewDefinition,
): boolean => {
  if (oldDefinition.filter?.type !== newDefinition.filter?.type) {
    return true;
  }

  const oldFilters = (oldDefinition.filter?.criteria ?? []).filter(
    FieldFilter.isValid,
  );
  const newFilters = (newDefinition.filter?.criteria ?? []).filter(
    FieldFilter.isValid,
  );

  if (
    oldFilters.length !== newFilters.length ||
    ApiFilter.toQueryStringFormat(oldFilters) !==
      ApiFilter.toQueryStringFormat(newFilters)
  ) {
    return true;
  }

  return false;
};

export type ViewFunction = (
  view:
    | CreatableView<DashboardFiltersViewDefinition>
    | View<DashboardFiltersViewDefinition>,
) => Promise<View<DashboardFiltersViewDefinition>>;

export const viewActionWithSourceId = (
  viewAction: ViewFunction,
  dashboardId: number,
): ViewFunction => {
  return (
    view:
      | CreatableView<DashboardFiltersViewDefinition>
      | View<DashboardFiltersViewDefinition>,
  ) => {
    const viewWithSource = {
      ...view,
      viewSourceId: dashboardId,
    };

    return viewAction(viewWithSource);
  };
};
